import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Subscription, timer } from 'rxjs';
import { catchError, switchMap, takeWhile } from 'rxjs/operators';

import { HouseExemplarService } from '../../house-exemplars/house-exemplar.service';
import {
  HouseExemplar,
  HouseExemplarKind,
  HouseExemplarLanguage,
} from '../../../interfaces/house-exemplar.interface';
import { NotifierService } from '../../../shared/notifier.service';

/**
 * Studio pane for managing a client's house-style exemplars.
 *
 * Self-contained: lists the client's exemplars (with a status chip per row),
 * offers an upload form (kind + language + PDF), lets the admin review and
 * edit a draft's distilled style guide, then activate or delete it.
 *
 * Uploads create a 'processing' row while the backend distills the PDF; the
 * pane polls every few seconds until no row is processing, mirroring the
 * Studio's template polling.
 */
@Component({
  selector: 'bst-cv-studio-pane-house',
  templateUrl: './studio-pane-house.component.html',
  styleUrls: ['./studio-pane-house.component.scss'],
  standalone: false,
})
export class StudioPaneHouseComponent implements OnInit, OnDestroy {

  @Input() public clientId!: number;

  public readonly kinds: HouseExemplarKind[] = ['cv', 'letter'];
  public readonly languages: HouseExemplarLanguage[] = ['nl', 'en'];

  public readonly maxBytes = 10 * 1024 * 1024;
  public readonly allowedMime = 'application/pdf';

  public exemplars: HouseExemplar[] = [];
  public loading = false;
  public error: string | null = null;

  public uploadForm: FormGroup;
  public file: File | null = null;
  public fileError: string | null = null;
  public uploading = false;
  public dragging = false;

  /** Per-row in-progress style_guide edits, keyed by exemplar id. */
  public guideEdits: { [id: number]: string } = {};
  /** Per-row busy flags so a Save/Activate spinner is scoped to its own row. */
  public savingId: number | null = null;
  public activatingId: number | null = null;

  private pollSub: Subscription | null = null;
  private subs: Subscription[] = [];

  constructor(
    private readonly fb: FormBuilder,
    private readonly service: HouseExemplarService,
    private readonly toastr: NbToastrService,
    private readonly translate: TranslateService,
    private readonly notifier: NotifierService,
  ) {
    this.uploadForm = this.fb.group({
      kind:     ['cv' as HouseExemplarKind, Validators.required],
      language: ['nl' as HouseExemplarLanguage, Validators.required],
    });
  }

  ngOnInit(): void {
    if (this.clientId) {
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    if (this.pollSub) { this.pollSub.unsubscribe(); }
  }

  public get canUpload(): boolean {
    return !this.uploading && !!this.file && this.uploadForm.valid;
  }

  public isDraft(e: HouseExemplar): boolean {
    return e.status === 'draft';
  }

  public hasLeakWarnings(e: HouseExemplar): boolean {
    return Array.isArray(e.leak_warnings) && e.leak_warnings.length > 0;
  }

  // ------------------------------------------------------------------
  // File pick + validation
  // ------------------------------------------------------------------

  public onFilePicked(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.file = this.validateFile(input.files?.[0] ?? null);
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragging = true;
  }

  public onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragging = false;
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragging = false;
    this.file = this.validateFile(event.dataTransfer?.files?.[0] ?? null);
  }

  private validateFile(file: File | null): File | null {
    this.fileError = null;
    if (!file) { return null; }
    if (file.type !== this.allowedMime) {
      this.fileError = this.translate.instant('cv_house.errors.pdf_only')
        || 'Only PDF files are accepted.';
      return null;
    }
    if (file.size > this.maxBytes) {
      this.fileError = this.translate.instant('cv_house.errors.file_too_large')
        || 'That file is too large. The limit is 10 MB.';
      return null;
    }
    return file;
  }

  // ------------------------------------------------------------------
  // Load + poll
  // ------------------------------------------------------------------

  private load(): void {
    this.loading = true;
    this.error = null;
    const sub = this.service.list(this.clientId).subscribe({
      next: rows => {
        this.exemplars = rows;
        this.seedGuideEdits(rows);
        this.loading = false;
        if (rows.some(r => r.status === 'processing')) { this.startPolling(); }
      },
      error: err => {
        console.error('studio-pane-house: load failed', err);
        this.error = this.translate.instant('cv_house.errors.load_failed')
          || 'Could not load the house examples.';
        this.loading = false;
      },
    });
    this.subs.push(sub);
  }

  /**
   * Seed the per-row edit buffer from the server's style_guide, but keep any
   * in-progress edit the admin is mid-typing so a poll refresh cannot wipe
   * unsaved keystrokes.
   */
  private seedGuideEdits(rows: HouseExemplar[]): void {
    for (const r of rows) {
      if (this.guideEdits[r.id] === undefined) {
        this.guideEdits[r.id] = r.style_guide ?? '';
      }
    }
  }

  private startPolling(): void {
    if (this.pollSub) { return; }
    // Swallow transient errors with EMPTY so takeWhile never sees a stray
    // result and stops the timer early. A sustained outage leaves rows in
    // 'processing'; the admin can refresh. Background refresh, so no toast.
    this.pollSub = timer(5000, 5000).pipe(
      switchMap(() => this.service.list(this.clientId).pipe(
        catchError(err => {
          console.error('studio-pane-house: poll failed', err);
          return EMPTY;
        }),
      )),
      takeWhile(rows => rows.some(r => r.status === 'processing'), true),
    ).subscribe(rows => {
      this.exemplars = rows;
      this.seedGuideEdits(rows);
      if (!rows.some(r => r.status === 'processing') && this.pollSub) {
        this.pollSub.unsubscribe();
        this.pollSub = null;
      }
    });
  }

  // ------------------------------------------------------------------
  // Upload
  // ------------------------------------------------------------------

  public submitUpload(): void {
    if (!this.canUpload || !this.file) {
      if (!this.file) {
        this.fileError = this.translate.instant('cv_house.errors.file_required')
          || 'Choose a PDF to upload.';
      }
      return;
    }

    this.uploading = true;
    const v = this.uploadForm.value;
    const sub = this.service.upload(this.clientId, v.kind, v.language, this.file).subscribe({
      next: created => {
        this.uploading = false;
        this.file = null;
        this.fileError = null;
        this.exemplars = [created, ...this.exemplars];
        this.seedGuideEdits([created]);
        this.toastr.success(
          this.translate.instant('cv_house.uploaded') || 'Upload received. Distilling the style.',
          this.translate.instant('cv_house.title') || 'House examples'
        );
        if (created.status === 'processing') { this.startPolling(); }
      },
      error: (err: HttpErrorResponse) => {
        this.uploading = false;
        console.error('studio-pane-house: upload failed', err);
        if (err?.status === 422 && err.error?.errors) {
          const errors = err.error.errors as { [field: string]: string[] };
          const first = Object.values(errors)[0]?.[0];
          this.fileError = first || (this.translate.instant('cv_house.errors.upload_failed')
            || 'Could not upload the file.');
        } else {
          this.toastr.danger(
            this.translate.instant('cv_house.errors.upload_failed') || 'Could not upload the file.',
            this.translate.instant('error') || 'Error'
          );
        }
      },
    });
    this.subs.push(sub);
  }

  // ------------------------------------------------------------------
  // Review (edit draft) + activate + delete
  // ------------------------------------------------------------------

  public saveGuide(e: HouseExemplar): void {
    if (this.savingId !== null) { return; }
    const guide = this.guideEdits[e.id] ?? '';
    this.savingId = e.id;
    const sub = this.service.updateGuide(this.clientId, e.id, guide).subscribe({
      next: updated => {
        this.savingId = null;
        this.replaceRow(updated);
        this.toastr.success(
          this.translate.instant('cv_house.guide_saved') || 'Style guide saved.',
          this.translate.instant('cv_house.title') || 'House examples'
        );
      },
      error: err => {
        this.savingId = null;
        console.error('studio-pane-house: save guide failed', err);
        this.toastr.danger(
          this.translate.instant('cv_house.errors.save_failed') || 'Could not save the style guide.',
          this.translate.instant('error') || 'Error'
        );
      },
    });
    this.subs.push(sub);
  }

  public activate(e: HouseExemplar): void {
    if (this.activatingId !== null) { return; }
    this.activatingId = e.id;
    const sub = this.service.activate(this.clientId, e.id).subscribe({
      next: updated => {
        this.activatingId = null;
        // Activating archives the prior active row of the same kind+language,
        // so refresh the whole list to reflect that server-side change.
        this.replaceRow(updated);
        this.load();
        this.toastr.success(
          this.translate.instant('cv_house.activated') || 'House example activated.',
          this.translate.instant('cv_house.title') || 'House examples'
        );
      },
      error: err => {
        this.activatingId = null;
        console.error('studio-pane-house: activate failed', err);
        this.toastr.danger(
          this.translate.instant('cv_house.errors.activate_failed') || 'Could not activate the house example.',
          this.translate.instant('error') || 'Error'
        );
      },
    });
    this.subs.push(sub);
  }

  public async confirmDelete(e: HouseExemplar): Promise<void> {
    const ok = await this.notifier.confirm(
      'cv_house.delete_confirm_title',
      this.translate.instant('cv_house.delete_confirm_body'),
      'cv_house.delete_button',
      'danger',
    );
    if (!ok) { return; }
    const sub = this.service.destroy(this.clientId, e.id).subscribe({
      next: () => {
        this.exemplars = this.exemplars.filter(r => r.id !== e.id);
        delete this.guideEdits[e.id];
        this.toastr.success(
          this.translate.instant('cv_house.deleted') || 'House example deleted.',
          this.translate.instant('cv_house.title') || 'House examples'
        );
      },
      error: err => {
        console.error('studio-pane-house: delete failed', err);
        this.toastr.danger(
          this.translate.instant('cv_house.errors.delete_failed') || 'Could not delete the house example.',
          this.translate.instant('error') || 'Error'
        );
      },
    });
    this.subs.push(sub);
  }

  private replaceRow(updated: HouseExemplar): void {
    this.exemplars = this.exemplars.map(r => r.id === updated.id ? updated : r);
    this.guideEdits[updated.id] = updated.style_guide ?? this.guideEdits[updated.id] ?? '';
  }
}
